/**
 * 用量统计（Alice 页面 modelUsage 的复刻）：按模型与北京时间日期范围，查看每个账号的 Token 消耗。
 *
 * 背景：运营核对用量时要回答「某个模型这段时间谁用得最多」。Alice 以用户为行，本仓以账号为行
 *   （Key 与账单都挂在账号上）；「排除内部账号」对应「排除仅记账请求」。
 * 设计意图：筛选条件变化即重新加载并回到第一页；模型下拉取后端给的全量选项，只填一次，筛选时不闪动。
 *   占比的分母是当前筛选结果的总 Token，不是全站。
 * 关键约束：只统计成功请求；日期留空为全部历史；一页 20 行。
 */
import { useEffect, useState } from 'react'
import { bizApi, type ModelUsage as ModelUsageData } from '@ap/api-client/admin-biz'
import { fmtInt } from '@ap/api-client/money'
import { A } from '@ap/i18n/admin'
import { BIZ } from '@ap/i18n/admin-biz'
import { errorText } from '@ap/i18n/common'
import { intlTag, useI18n } from '@ap/i18n'
import { Link, fmtDate, useLoad, useToast } from '@ap/ui'
import { FIELD, beijingDate, compactTokens } from '../../alice/biz-kit.tsx'
import { OpsNote, OpsPage, Pager, Stat, Stats, TableWrap, Toolbar } from '../../alice/kit.tsx'
import { useUsd } from '../../shared.tsx'

const LIMIT = 20

export function ModelUsage() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  const usd = useUsd()
  const toast = useToast()
  const [model, setModel] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [excl, setExcl] = useState(false)
  const [offset, setOffset] = useState(0)
  const [options, setOptions] = useState<string[]>([])

  const load = useLoad(
    () => bizApi.modelUsage({ model_id: model, start, end, exclude_internal: excl, limit: LIMIT, offset }),
    [model, start, end, excl, offset],
  )
  const d: ModelUsageData | null = load.data

  // 模型下拉只填一次（后端按近期请求量降序），避免切换筛选时选项闪动
  useEffect(() => {
    if (d && options.length === 0) setOptions(d.model_options)
  }, [d, options.length])
  useEffect(() => {
    if (load.error) toast.error(load.error)
  }, [load.error, toast])

  const reset = (fn: () => void) => {
    fn()
    setOffset(0)
  }
  const setRange = (days: number) =>
    reset(() => {
      setStart(beijingDate(-days))
      setEnd(beijingDate(0))
    })

  const s = d?.summary
  const total = s?.total_tokens ?? 0
  const tok = (n: number) => compactTokens(n)

  return (
    <OpsPage eyebrow="USAGE ANALYTICS" title={t(A.nav.modelUsage)} desc={t(BIZ.mu.desc)} chip={t(BIZ.mu.chip)}>
      <Stats>
        <Stat label={t(BIZ.mu.totalTokens)} value={s ? tok(total) : '—'} sub={s ? t(BIZ.mu.ioSub, { i: tok(s.input_tokens), o: tok(s.output_tokens) }) : '—'} />
        <Stat
          label={t(BIZ.mu.accounts)}
          value={s ? fmtInt(s.accounts, tag) : '—'}
          sub={s && s.accounts ? t(BIZ.mu.perOrg, { n: tok(total / s.accounts) }) : '—'}
        />
        <Stat
          label={t(BIZ.mu.requests)}
          value={s ? fmtInt(s.requests, tag) : '—'}
          sub={s && s.requests ? t(BIZ.mu.perReq, { n: tok(total / s.requests) }) : '—'}
        />
        <Stat
          label={t(BIZ.mu.cost)}
          value={s ? usd(s.cost_units) : '—'}
          sub={s && s.input_tokens ? t(BIZ.mu.cacheSub, { p: ((s.cached_tokens / s.input_tokens) * 100).toFixed(1) }) : '—'}
        />
      </Stats>

      <Toolbar count={s ? t(BIZ.mu.totalBadge, { n: s.accounts }) : undefined}>
        <select style={{ ...FIELD, padding: '8px 28px 8px 10px', cursor: 'pointer', maxWidth: 260 }} value={model} onChange={(e) => reset(() => setModel(e.target.value))}>
          <option value="">{t(BIZ.mu.allModels)}</option>
          {options.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <input type="date" style={FIELD} value={start} max={beijingDate(0)} onChange={(e) => reset(() => setStart(e.target.value))} aria-label={t(BIZ.mu.start)} />
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t(BIZ.common.to)}</span>
        <input type="date" style={FIELD} value={end} max={beijingDate(0)} onChange={(e) => reset(() => setEnd(e.target.value))} aria-label={t(BIZ.mu.end)} />
        <button className="btn btn-secondary btn-sm" onClick={() => setRange(0)}>
          {t(BIZ.mu.today)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setRange(6)}>
          {t(BIZ.mu.last7)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setRange(29)}>
          {t(BIZ.mu.last30)}
        </button>
        <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={excl} onChange={(e) => reset(() => setExcl(e.target.checked))} />
          {t(BIZ.mu.exclMetered)}
        </label>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() =>
            reset(() => {
              setModel('')
              setStart('')
              setEnd('')
              setExcl(false)
            })
          }
        >
          {t(BIZ.common.clear)}
        </button>
      </Toolbar>

      <OpsNote>{t(BIZ.mu.note)}</OpsNote>

      <TableWrap
        head={
          <>
            <th>#</th>
            <th>{t(BIZ.mu.colOrg)}</th>
            <th>{t(BIZ.mu.colRequests)}</th>
            <th>{t(BIZ.mu.colInput)}</th>
            <th>{t(BIZ.mu.colOutput)}</th>
            <th>{t(BIZ.mu.colCached)}</th>
            <th>{t(BIZ.mu.colTotal)}</th>
            <th>{t(BIZ.mu.colShare)}</th>
            <th>{t(BIZ.mu.colCost)}</th>
            <th>{t(BIZ.mu.colLast)}</th>
          </>
        }
        pager={d && <Pager offset={offset} limit={LIMIT} total={d.total_accounts} onChange={setOffset} />}
      >
        {load.error && !d ? (
          <tr>
            <td colSpan={10} className="empty">
              {errorText(load.error, locale)}
            </td>
          </tr>
        ) : !d ? (
          <tr>
            <td colSpan={10} className="empty">
              {t(BIZ.common.loading)}
            </td>
          </tr>
        ) : d.accounts.length === 0 ? (
          <tr>
            <td colSpan={10} className="empty">
              {t(BIZ.mu.empty)}
            </td>
          </tr>
        ) : (
          d.accounts.map((u, i) => {
            const pct = total ? (u.total_tokens / total) * 100 : 0
            return (
              <tr key={u.account_id}>
                <td style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{offset + i + 1}</td>
                <td>
                  <div style={{ fontWeight: 500 }}>
                    <Link href={`/accounts/${u.account_id}`}>{u.name}</Link>
                    {u.is_internal && (
                      <>
                        {' '}
                        <span className="badge badge-off" style={{ fontSize: 10 }}>
                          {t(BIZ.mu.metered)}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="mono" style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                    ID: {u.account_id}
                  </div>
                </td>
                <td style={{ fontFamily: 'var(--mono)' }}>{fmtInt(u.requests, tag)}</td>
                <td style={{ fontFamily: 'var(--mono)' }}>{tok(u.input_tokens)}</td>
                <td style={{ fontFamily: 'var(--mono)' }}>{tok(u.output_tokens)}</td>
                <td style={{ fontFamily: 'var(--mono)', color: 'var(--text-tertiary)' }}>{tok(u.cached_tokens)}</td>
                <td style={{ fontFamily: 'var(--mono)', fontWeight: 600 }}>{tok(u.total_tokens)}</td>
                <td style={{ minWidth: 110 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ flex: 1, height: 5, background: 'var(--bg-subtle)', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: 5, borderRadius: 3, background: '#3b82f6', width: `${Math.max(pct, 0.5).toFixed(1)}%` }} />
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>{pct.toFixed(1)}%</span>
                  </div>
                </td>
                <td style={{ fontSize: 12 }}>{usd(u.cost_units)}</td>
                <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-tertiary)' }}>{fmtDate(u.last_used_at, locale)}</td>
              </tr>
            )
          })
        )}
      </TableWrap>
    </OpsPage>
  )
}
